<template>
	<ui-root v-if="loaded" :data="favoriteMail" :part="form" ref="frm" :errs="errors" :actions="dialogControls(env)"></ui-root>
</template>

<script setup lang="ts">
import {onMounted, onUnmounted, ref, watch, reactive} from "vue";
import {connectEventBus, injectEnv} from "@fd/Environment";
import type {FavoriteMail} from "@t/mail/FavoriteMail.ts";
import type {WithMeta} from "@t/fd/WithMeta";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition";
import FormPart from "@fd/containers/FormPart.vue";
import type {ErrorMap} from "@fd/PartInjectKeys";
import {NetworkResponse} from "@fd/Types";
import {useRouter,useRoute} from "vue-router";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import UiRoot from "@fd/containers/UiRoot.vue";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {getIntAttParam} from "@/router/Router.ts";
import {useVirtualRoute} from "@/router/virtual/VirtualRouter.ts";

const env = injectEnv();
const navStore = useNavigationStore();
const router = useRouter();
const route = useVirtualRoute();

const loaded = ref<boolean>(false);

const errors = reactive<ErrorMap>([]);
const favoriteMail = ref<FavoriteMail|null>(null);
const form = ref<InterfaceRoot<FormDefinition>|null>(null);
const bus = connectEventBus("FavoriteMailPage");
const frm = ref(null);

function receivedData(data:FavoriteMail & WithMeta):void {
	if (data.META) {
		form.value = data.META.form;
		delete data.META;
	}
	favoriteMail.value = data;
	loaded.value = true;
	if (favoriteMail.value && favoriteMail.value.id>0) {
		navStore.replace(favoriteMail.value.name);
	}
}

function loadData() {
	let endpoint = null;
	const id = route.query.id;
	if (id > 0) {
		endpoint = "api/favoritemails/"+id+"?meta=true";
	}
	else {
		endpoint = "api/favoritemails/init?meta=true";
	}
	return env.net.fetch(endpoint)
			.then((result) => result.json())
			.then(receivedData);
}

function saveData():Promise<NetworkResponse> {
    return env.net.fetch("api/favoritemails/"+favoriteMail.value.id, "PUT", favoriteMail.value);
}

function createData():Promise<NetworkResponse> {
    return env.net.fetch("api/favoritemails", "POST", favoriteMail.value);
}

function sendData():Promise<NetworkResponse> {
	return (!favoriteMail.value.id || favoriteMail.value.id===0)?createData():saveData();
}

bus.onCommand(["save", "saveAndBack"], async (cmd) => {
	errors.length = 0;
	const response = await sendData();
	if (response.ok) {
		if (cmd === "save") {
			const data = await response.json();
			receivedData(data);
			if (frm.value) {
				frm.value.setDirty(false);
			}
		} else {
			// Go back
			router.back();
		}
		return "ok";
	} else {
		const data = await response.json();
		if (Array.isArray(data?.problems)) {
			for (let problem of data.problems) {
				errors.push(problem);
			}
		}
		console.error(response);
		return "fail";
	}
});


watch(() => route.query.id, loadData, { immediate: true });
navStore.defineMenu((mb) => mb.standardMenu());

onMounted(() => {
	navStore.forward(env.msg("title.createFavoriteMail"));
});
</script>

<style>

</style>

